import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { finalize } from 'rxjs';

import { ModalService } from '@services/components/ModalService/modal.service';
import { ToastService } from '@services/components/ToastService/toast.service';
import {
  ProductWithoutBox,
  ShippingBox,
  ShippingBoxRequest,
  ShippingBoxSaveResponse,
} from '@services/shipping/interfaces/shipping.interfaces';
import { ShippingService } from '@services/shipping/shipping.service';
import { GenericConfirmModalData } from '@shared/components/UI/generic-confirm-modal/generic-confirm-modal.component';
import { buildDeactivateShippingBoxConfirmModal } from '@shared/constants/modal.constants';
import { buildSuccessShippingBoxToast } from '@shared/constants/toasts.constants';

const MAX_DIMENSION_CM = 200;
const MAX_WEIGHT_GRAMS = 1_000_000;
const GENERIC_SAVE_ERROR = 'No pudimos guardar la caja. Intenta nuevamente.';

@Component({
  selector: 'app-shipping-boxes',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './shipping-boxes.html',
  styleUrls: ['./shipping-boxes.css'],
})
export class ShippingBoxesComponent implements OnInit {
  public readonly shippingBoxes = signal<ShippingBox[]>([]);
  public readonly fillFactorPercentage = signal<number | null>(null);
  public readonly isLoading = signal(true);
  public readonly hasLoadError = signal(false);
  public readonly viewMode = signal<'list' | 'form'>('list');
  public readonly isSubmitting = signal(false);
  public readonly errorMessage = signal<string | null>(null);
  public readonly productsWithoutBox = signal<ProductWithoutBox[]>([]);
  public readonly togglingBoxId = signal<string | null>(null);

  public editingBoxId: string | null = null;

  public readonly boxForm = inject(FormBuilder).nonNullable.group({
    code: ['', [Validators.required, Validators.maxLength(10), Validators.pattern(/^[A-Za-z0-9-]+$/)]],
    label: ['', [Validators.required, Validators.maxLength(50)]],
    lengthCm: [0, [Validators.required, Validators.min(1), Validators.max(MAX_DIMENSION_CM)]],
    widthCm: [0, [Validators.required, Validators.min(1), Validators.max(MAX_DIMENSION_CM)]],
    heightCm: [0, [Validators.required, Validators.min(1), Validators.max(MAX_DIMENSION_CM)]],
    maxWeightGrams: [0, [Validators.required, Validators.min(1), Validators.max(MAX_WEIGHT_GRAMS)]],
  });

  private readonly shippingService = inject(ShippingService);
  private readonly toastService = inject(ToastService);
  private readonly modalService = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);

  public ngOnInit(): void {
    this.loadShippingBoxes();
  }

  public openNewForm(): void {
    this.editingBoxId = null;
    this.errorMessage.set(null);
    this.boxForm.reset({ code: '', label: '', lengthCm: 0, widthCm: 0, heightCm: 0, maxWeightGrams: 0 });
    this.viewMode.set('form');
  }

  public openEditForm(box: ShippingBox): void {
    this.editingBoxId = box.id;
    this.errorMessage.set(null);
    this.boxForm.reset({
      code: box.code,
      label: box.label,
      lengthCm: box.lengthCm,
      widthCm: box.widthCm,
      heightCm: box.heightCm,
      maxWeightGrams: box.maxWeightGrams,
    });
    this.viewMode.set('form');
  }

  public closeForm(): void {
    this.viewMode.set('list');
    this.errorMessage.set(null);
  }

  public onSubmit(): void {
    if (this.boxForm.invalid) {
      this.boxForm.markAllAsTouched();
      return;
    }

    const request: ShippingBoxRequest = this.boxForm.getRawValue();
    const isEditing = this.editingBoxId !== null;
    const request$ = this.editingBoxId
      ? this.shippingService.updateShippingBox(this.editingBoxId, request)
      : this.shippingService.createShippingBox(request);

    this.isSubmitting.set(true);
    this.errorMessage.set(null);

    request$
      .pipe(
        finalize(() => this.isSubmitting.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          this.handleSaved(response, isEditing);
          this.closeForm();
        },
        error: (error) => this.errorMessage.set(this.readErrorMessage(error)),
      });
  }

  public toggleActive(box: ShippingBox): void {
    if (this.togglingBoxId() !== null) {
      return;
    }

    if (!box.isActive) {
      this.saveActiveState(box, true);
      return;
    }

    this.modalService
      .openModal<boolean, GenericConfirmModalData>(buildDeactivateShippingBoxConfirmModal(box.label))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.reason === 'confirm') {
          this.saveActiveState(box, false);
        }
      });
  }

  public hasFieldError(controlName: keyof ShippingBoxRequest): boolean {
    const control = this.boxForm.controls[controlName];
    return control.invalid && control.touched;
  }

  private handleSaved(response: ShippingBoxSaveResponse, isEditing: boolean): void {
    this.productsWithoutBox.set(response.productsWithoutBox);
    this.toastService.open(buildSuccessShippingBoxToast(isEditing));
    this.loadShippingBoxes();
  }

  private loadShippingBoxes(): void {
    this.isLoading.set(true);
    this.hasLoadError.set(false);

    this.shippingService.getShippingBoxes()
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ boxes, fillFactor }) => {
          this.shippingBoxes.set(boxes);
          this.fillFactorPercentage.set(Number.isFinite(fillFactor) ? Math.round(fillFactor * 100) : null);
        },
        error: () => this.hasLoadError.set(true),
      });
  }

  private readErrorMessage(error: { error?: { message?: string | string[] } }): string {
    const message = error.error?.message;
    return (Array.isArray(message) ? message.join('. ') : message) ?? GENERIC_SAVE_ERROR;
  }

  private saveActiveState(box: ShippingBox, isActive: boolean): void {
    this.errorMessage.set(null);
    this.togglingBoxId.set(box.id);

    this.shippingService
      .updateShippingBox(box.id, { isActive })
      .pipe(
        finalize(() => this.togglingBoxId.set(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => this.handleSaved(response, true),
        error: (error) => this.errorMessage.set(this.readErrorMessage(error)),
      });
  }
}
